import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import { DSButton } from "@components/design-system/molecules";
import {
  faCopy,
  faEnvelope,
  faFilePdf,
} from "@fortawesome/free-regular-svg-icons";
import { usePimsExportFormat } from "@hooks";
import React, { useState } from "react";
import { useParams } from "react-router";

import { NOTE_FIELDS } from "../../graphql/fragments/note";
import { useNoteDownload } from "../common/clinical/NotePDF";
import { copyAll, copyClientSummary } from "../notebook/note/utils";

const GET_NOTE = gql`
  ${NOTE_FIELDS}
  query getNote($uuid: String) {
    note(uuid: $uuid) {
      patient {
        uuid
        name
        lastNameOfOwner
        sex
        species
      }
      user {
        uuid
        firstName
        lastName
        name
        roleId
        account {
          uuid
          logoFileLink
          headerInfoPdf
        }
      }
      autoTemplates {
        uuid
        name
      }
      ...NoteFields
      keyHistory
      keyMedications
      vaccinationStatus
    }
  }
`;

export function ExportOptionButton({
  children,
  onClick,
  cooldown = false,
  dataTestId,
  icon,
}) {
  const [isCooldown, setIsCooldown] = useState(false);

  const handleClick = () => {
    if (!isCooldown) {
      onClick();
      if (cooldown) {
        setIsCooldown(true);
        setTimeout(() => {
          setIsCooldown(false);
        }, 5000);
      }
    }
  };

  return (
    <DSButton
      onClick={handleClick}
      dataTestId={dataTestId}
      disabled={isCooldown}
      icon={icon}
      size="md"
    >
      {children}
    </DSButton>
  );
}

export function ExportNoteInline({
  getValues,
  isClientSummaryMode = false,
  clientSummary,
  note,
  onEmailClick,
  onExportSuccess,
}) {
  const { noteUUID } = useParams();
  const pimsFormat = usePimsExportFormat();
  const { downloadNote, downloadClientSummary } = useNoteDownload();

  const [getNote] = useLazyQuery(GET_NOTE, {
    fetchPolicy: "network-only",
  });

  const handleExportButton = () => {
    getNote({
      variables: {
        uuid: noteUUID,
      },
    })
      .then((response) => {
        if (isClientSummaryMode) {
          downloadClientSummary(
            clientSummary,
            response?.data?.note?.user,
          );
        } else {
          downloadNote(
            response?.data?.note,
            response?.data?.note?.patient,
            response?.data?.note?.user,
          ).catch((response) => {
            console.log(response);
          });
        }
        onExportSuccess?.();
      })
      .catch((e) => {
        alert(
          "error",
          "An error has occurred. Please try again later.",
        );
        console.log(e);
      });
  };

  const getNewestNoteData = () => {
    try {
      return getValues();
    } catch (error) {
      console.log(error);
      return note;
    }
  };

  const handleCopyText = () => {
    if (isClientSummaryMode) {
      copyClientSummary({
        clientSummary: clientSummary,
        format: pimsFormat,
      });
    } else {
      copyAll({
        note: getNewestNoteData(),
        isForSharedExport: true,
        format: pimsFormat,
      });
    }
    onExportSuccess?.();
  };

  return (
    <div className="flex-1 w-full flex flex-row flex-wrap items-center gap-sm mb-4">
      <ExportOptionButton
        onClick={handleCopyText}
        icon={faCopy}
        dataTestId="export-copy-button"
      >
        Copy Entire Note
      </ExportOptionButton>
      <ExportOptionButton
        onClick={handleExportButton}
        cooldown={true}
        icon={faFilePdf}
        dataTestId="export-pdf-button"
      >
        Download PDF
      </ExportOptionButton>
      {onEmailClick && (
        <ExportOptionButton
          onClick={onEmailClick}
          icon={faEnvelope}
          dataTestId="export-email-button"
        >
          Send Email
        </ExportOptionButton>
      )}
    </div>
  );
}
